import { useMemo, useState } from 'react';
import fixWebmDuration from 'fix-webm-duration';
import { toast } from 'sonner';
import { PageWrapper } from '@/layout/components/PageWrapper.tsx';
import { PageHeader } from '@/layout/components/PageHeader.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUser } from '@/hooks/useUser.ts';
import { useTranscribeAudioMutation, type TranscriptionModel, type TranscriptionResponseFormat } from '@/store/transcriptionApi.ts';

type UploadMode = 'upload' | 'record';

function formatSeconds(seconds: number | null): string {
  if (seconds === null) {
    return '-';
  }
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

async function getAudioDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    const objectUrl = URL.createObjectURL(file);
    audio.preload = 'metadata';
    audio.src = objectUrl;
    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) ? Math.ceil(audio.duration) : 0;
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(0);
    };
  });
}

export default function TranscriptionPage() {
  const { user } = useUser();
  const [transcribeAudio, { isLoading }] = useTranscribeAudioMutation();

  const [uploadMode, setUploadMode] = useState<UploadMode>('upload');
  const [model, setModel] = useState<TranscriptionModel>('regular');
  const [email, setEmail] = useState(user?.email || '');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [transcript, setTranscript] = useState('');

  const [responseFormat, setResponseFormat] = useState<TranscriptionResponseFormat>('text');
  const [prompt, setPrompt] = useState('');

  const [isRecording, setIsRecording] = useState(false);
  const [recordingLabel, setRecordingLabel] = useState('');
  const [recordedFileName, setRecordedFileName] = useState('');

  const isLongFile = (durationSeconds || 0) >= 60;
  const canSubmit = useMemo(() => {
    if (!selectedFile) {
      return false;
    }
    if (isLongFile && model === 'regular') {
      return false;
    }
    if (isLongFile && model === 'ai' && !email.trim()) {
      return false;
    }
    return true;
  }, [selectedFile, isLongFile, model, email]);

  async function onFilePicked(file: File | null) {
    setSelectedFile(file);
    setTranscript('');
    if (!file) {
      setDurationSeconds(null);
      return;
    }
    const duration = await getAudioDurationSeconds(file);
    setDurationSeconds(duration);
    if (duration >= 60 && model === 'regular') {
      setModel('ai');
      toast.info('בקובץ של דקה ומעלה ניתן לתמלל רק במודל AI');
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const startTs = Date.now();
      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const duration = Date.now() - startTs;
        const rawBlob = new Blob(chunks, { type: mediaRecorder.mimeType || 'audio/webm' });
        const fixedBlob = await fixWebmDuration(rawBlob, duration, { logger: false });
        const file = new File([fixedBlob], `recording-${Date.now()}.webm`, {
          type: fixedBlob.type || 'audio/webm',
        });
        await onFilePicked(file);
        setRecordedFileName(file.name);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingLabel('מקליט... לחץ עצור לסיום');

      (window as Window & { __transcriptionRecorder?: MediaRecorder }).__transcriptionRecorder = mediaRecorder;
    } catch {
      toast.error('לא ניתן לגשת למיקרופון בדפדפן');
    }
  }

  function stopRecording() {
    const mediaRecorder = (window as Window & { __transcriptionRecorder?: MediaRecorder }).__transcriptionRecorder;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    setIsRecording(false);
    setRecordingLabel('ההקלטה נשמרת...');
    setTimeout(() => setRecordingLabel(''), 1200);
  }

  async function handleSubmit() {
    if (!selectedFile) {
      toast.error('יש לבחור קובץ או לבצע הקלטה');
      return;
    }

    try {
      const result = await transcribeAudio({
        file: selectedFile,
        model,
        email,
        ...(model === 'ai' ? { response_format: responseFormat, prompt: prompt.trim() || undefined } : {}),
      }).unwrap();

      if (result.mode === 'sync') {
        setTranscript(result.transcript || '');
        toast.success('התמלול הושלם בהצלחה');
        return;
      }

      setTranscript('');
      toast.success(`הקובץ נשלח לעיבוד. התוצאה תישלח למייל: ${result.email || email}`);
    } catch (err: unknown) {
      const msg =
        (err as { data?: { msg?: string } })?.data?.msg ||
        'אירעה שגיאה בתהליך התמלול';
      toast.error(msg);
    }
  }

  return (
    <PageWrapper>
      <PageHeader title="תמלול קבצי שמע" />

      <Card className="max-w-3xl w-full">
        <CardHeader>
          <CardTitle>העלאה או הקלטה לתמלול</CardTitle>
          <CardDescription>
            קבצים הקצרים מדקה מתומללים מיידית ומוצגים כאן. קבצים מדקה ומעלה נשלחים לעיבוד AI אסינכרוני ונשלחים למייל.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>שיטת קלט</Label>
              <Select value={uploadMode} onValueChange={(value) => setUploadMode(value as UploadMode)}>
                <SelectTrigger>
                  <SelectValue placeholder="בחר שיטת קלט" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upload">העלאת קובץ</SelectItem>
                  <SelectItem value="record">הקלטה בדפדפן</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>מודל תמלול</Label>
              <Select value={model} onValueChange={(value) => setModel(value as TranscriptionModel)}>
                <SelectTrigger>
                  <SelectValue placeholder="בחר מודל" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular" disabled={isLongFile}>רגיל</SelectItem>
                  <SelectItem value="ai">AI</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {model === 'ai' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>פורמט תגובה</Label>
                <Select value={responseFormat} onValueChange={(value) => setResponseFormat(value as TranscriptionResponseFormat)}>
                  <SelectTrigger>
                    <SelectValue placeholder="בחר פורמט" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">טקסט רגיל</SelectItem>
                    <SelectItem value="verbose_json">JSON מפורט (עם מקטעים)</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="srt">SRT (כתוביות)</SelectItem>
                    <SelectItem value="vtt">VTT (כתוביות Web)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>הנחיה לתמלול (Prompt)</Label>
                <Input
                  type="text"
                  placeholder="הנחיה אופציונלית לשיפור דיוק התמלול"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>
            </div>
          )}

          {uploadMode === 'upload' && (
            <div className="space-y-2">
              <Label>קובץ שמע</Label>
              <Input
                type="file"
                accept="audio/*"
                onChange={(e) => onFilePicked(e.target.files?.[0] || null)}
              />
            </div>
          )}

          {uploadMode === 'record' && (
            <div className="space-y-3">
              <Label>הקלטה</Label>
              <div className="flex items-center gap-3">
                {!isRecording ? (
                  <Button type="button" onClick={startRecording}>התחל הקלטה</Button>
                ) : (
                  <Button type="button" variant="destructive" onClick={stopRecording}>עצור הקלטה</Button>
                )}
                {recordingLabel && <span className="text-sm text-muted-foreground">{recordingLabel}</span>}
              </div>
              {recordedFileName && (
                <div className="text-sm text-muted-foreground">קובץ מוקלט: {recordedFileName}</div>
              )}
            </div>
          )}

          <div className="rounded-md border p-3 bg-muted/20 text-sm space-y-1">
            <div>משך מזוהה: {formatSeconds(durationSeconds)}</div>
            {isLongFile && (
              <div className="text-amber-700">בקובץ של דקה ומעלה ניתן לתמלל רק במודל AI והתוצאה נשלחת במייל.</div>
            )}
          </div>

          <div className="space-y-2">
            <Label>כתובת מייל לקבלת תוצאה (לקבצים ארוכים)</Label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="text-xs text-muted-foreground">
              המייל שלך: {user?.email || 'לא מוגדר'}
            </div>
          </div>

          <Button type="button" onClick={handleSubmit} disabled={!canSubmit || isLoading} className="w-full">
            {isLoading ? 'מבצע תמלול...' : 'התחל תמלול'}
          </Button>

          <div className="space-y-2">
            <Label>תמלול</Label>
            <Textarea
              value={transcript}
              readOnly
              rows={10}
              placeholder="תוצאת התמלול תופיע כאן בקבצים באורך של עד דקה"
              className="font-mono"
            />
          </div>
        </CardContent>
      </Card>
    </PageWrapper>
  );
}
